import React, { useEffect, useState, useRef } from 'react';
import { getCurrencyDisplay } from '../utils/currency';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

interface BillData {
  bill_number: string;
  customer_name: string;
  grand_total: number;
  payment_mode: string;
  bill_id: number;
  upi_id?: string;
  org_id: number;
  currency?: string;
  qr_data_url?: string;
  qr_code?: string;
  qr_image?: string;
  qr_image_url?: string;
  payment_link?: string;
}

export default function DisplayPage() {
  const [bill, setBill] = useState<BillData | null>(null);
  const [qrUrl, setQrUrl] = useState<string>('');
  const [qrContent, setQrContent] = useState<string>('');
  const [paid, setPaid] = useState(false);
const [failed, setFailed] = useState(false);
const [orgCurrency, setOrgCurrency] = useState('USD');
  const [connected, setConnected] = useState(false);
  const [displayToken, setDisplayToken] = useState<string>('');
  const [registerId, setRegisterId] = useState<string>('');
  const [inputToken, setInputToken] = useState('');
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const urlRegister = params.get('register_id');
    if (urlToken) {
      localStorage.setItem('display_token', urlToken);
      setDisplayToken(urlToken);
    } else {
      const saved = localStorage.getItem('display_token');
      if (saved) setDisplayToken(saved);
    }
    if (urlRegister) {
      localStorage.setItem('display_register_id', urlRegister);
      setRegisterId(urlRegister);
    } else {
      const savedRegister = localStorage.getItem('display_register_id');
      if (savedRegister) setRegisterId(savedRegister);
    }
  }, []);

  useEffect(() => {
    if (!displayToken) return;
    connect(displayToken, registerId);
    return () => esRef.current?.close();
  }, [displayToken, registerId]);

 useEffect(() => {
    if (!displayToken) return;
    fetch(`${BACKEND}/display/org-currency/${displayToken}`)
      .then(r => r.json())
      .then(d => setOrgCurrency(d.currency || 'USD'))
      .catch(() => {});
  }, [displayToken]);

  const connect = (tok: string, regId?: string) => {
    esRef.current?.close();
    const qs = regId ? `?register_id=${regId}` : '';
    const streamUrl = `${BACKEND}/display/stream/${tok}${qs}`;
    const es = new EventSource(streamUrl);
    esRef.current = es;
    es.onopen = () => setConnected(true);
    es.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === 'ping' || data.type === 'connected') return;
      if (data.type === 'paid') {
        setPaid(true);
        setFailed(false);
        return;
      }
      if (data.type === 'failed') {
        setFailed(true);
        return;
      }
      // New bill event
      setBill(data);
      setPaid(false);
      setFailed(false);
      setPaymentUnavailable(false);
      generateQR(data);
    };
    es.onerror = () => {
      setConnected(false);
      es.close();
      setTimeout(() => connect(tok, regId), 3000);
    };
  };

  const generateQR = async (data: BillData) => {
    setPaymentUnavailable(false);
    setQrUrl('');
    setQrContent('');

    // Cash needs no QR at all.
    if (data.payment_mode === 'cash') return;

    try {
      const QRCode = await import('qrcode');
      let qrContent = '';
      setQrIsPoster(false);

      // Use whatever QR was already generated and pushed with the bill —
      // this covers ledger tabs and members, which have no real invoice id.
      const pushedQrContent = (data as any).qr_code || (data as any).qr_data_url || (data as any).qr_image;
      const pushedQrImageUrl = (data as any).qr_image_url;
      const pushedPaymentLink = (data as any).payment_link;

      if (pushedQrContent) {
        // This is already a rendered image (data URL) from the sender's
        // side — show it directly. Don't put the raw base64 into
        // qrContent, or it prints as garbage text in the debug box below.
        setQrIsPoster(false);
        setQrUrl(pushedQrContent);
        setQrContent('');
        return;
      } else if (pushedQrImageUrl) {
        setQrIsPoster(true);
        setQrUrl(pushedQrImageUrl);
        setQrContent(pushedQrImageUrl);
        return;
      } else if (pushedPaymentLink) {
        qrContent = pushedPaymentLink;
      } else if (data.upi_id && data.payment_mode === 'upi') {
        qrContent = `upi://pay?pa=${encodeURIComponent(data.upi_id)}&pn=${encodeURIComponent(data.customer_name || 'Customer')}&am=${data.grand_total}&tn=${encodeURIComponent(data.bill_number || '')}&cu=INR`;
      } else if (data.bill_id && data.bill_id > 0) {
        // No raw merchant VPA on file — ask the backend for whichever the
        // connected gateway can give us.
        const gwRes = await fetch(`${BACKEND}/payments/bill/${data.bill_id}`);

        // Backend returns 404 (or configured:false) when no gateway/UPI
        // has been set up for this org — treat that as "not configured",
        // not a transient error to silently paper over.
        if (gwRes.status === 404) {
          setPaymentUnavailable(true);
          return;
        }
        if (!gwRes.ok) {
          throw new Error(`Gateway lookup failed: ${gwRes.status}`);
        }

        const gwData = await gwRes.json();
        if (gwData.configured === false) {
          setPaymentUnavailable(true);
          return;
        }

        if (gwData.qr_image_content) {
          setQrIsPoster(false);
          qrContent = gwData.qr_image_content;
        } else if (gwData.qr_image_url) {
          setQrIsPoster(true);
          setQrUrl(gwData.qr_image_url);
          setQrContent(gwData.qr_image_url);
          return;
        } else if (gwData.payment_link) {
          qrContent = gwData.payment_link;
        } else {
          // Gateway responded but gave us nothing usable.
          setPaymentUnavailable(true);
          return;
        }
      } else {
        // No pushed QR, no upi_id, and no real bill_id to look up — nothing to show.
        setPaymentUnavailable(true);
        return;
      }

      const url = await QRCode.default.toDataURL(qrContent, { width: 300, margin: 2 });
      setQrUrl(url);
      setQrContent(qrContent);
    } catch (e) {
      console.error('QR gen failed', e);
      setPaymentUnavailable(true);
    }
  };

  const handleSetToken = () => {
    if (!inputToken.trim()) return;
    localStorage.setItem('display_token', inputToken.trim());
    setDisplayToken(inputToken.trim());
  };

  const confirmPaid = async () => {
    if (!bill) return;
    setPaid(true);
    try {
      await fetch(`${BACKEND}/display/confirm-paid/${displayToken}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bill_id: bill.bill_id, invoice_number: bill.bill_number, register_id: registerId || undefined }),
      });
    } catch (e) { console.warn('confirm-paid failed', e); }
  };

  const [qrIsPoster, setQrIsPoster] = useState(false);
  const [paymentUnavailable, setPaymentUnavailable] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const sym = getCurrencyDisplay(orgCurrency, 'symbol');
  const bg = darkMode ? '#0f172a' : '#f1f5f9';
  const cardBg = darkMode ? '#1e293b' : '#ffffff';
  const textPrimary = darkMode ? '#f1f5f9' : '#0f172a';
  const textMuted = darkMode ? '#94a3b8' : '#64748b';
  const border = darkMode ? '#334155' : '#e2e8f0';

  if (!displayToken) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{ background:'#1e293b', borderRadius:20, padding:40, width:360, textAlign:'center', boxShadow:'0 32px 80px rgba(0,0,0,0.4)' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🖥️</div>
          <div style={{ fontSize:20, fontWeight:800, color:'#f1f5f9', marginBottom:6 }}>Counter Display</div>
          <div style={{ fontSize:12, color:'#94a3b8', marginBottom:24 }}>Enter your Display Token to start receiving bills</div>
          <input value={inputToken} onChange={e => setInputToken(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSetToken()}
            placeholder="Display Token"
            style={{ width:'100%', padding:'10px 14px', borderRadius:10, border:'1.5px solid #334155', background:'#0f172a', color:'#f1f5f9', fontSize:14, outline:'none', boxSizing:'border-box', marginBottom:12 }} />
          <button onClick={handleSetToken} style={{ width:'100%', padding:11, borderRadius:10, border:'none', background:'linear-gradient(135deg,#3b82f6,#6366f1)', color:'#fff', fontWeight:800, fontSize:14, cursor:'pointer' }}>
            Connect Display
          </button>
        </div>
      </div>
    );
  }

  

 return (
    <div style={{
      minHeight: '100vh',
      background: bg,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      transition: 'background 0.3s'
    }}>

      {/* Top bar */}
      <div style={{ position:'fixed', top:0, left:0, right:0, height:52, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 24px', background: darkMode ? 'rgba(15,23,42,0.98)' : 'rgba(255,255,255,0.98)', backdropFilter:'blur(12px)', borderBottom:`1px solid ${border}`, zIndex:100 }}>
        
        {/* Brand */}
        <div style={{ fontSize:11, fontWeight:700, color:'#0f172a', letterSpacing:1 }}>
          COUNTER DISPLAY
        </div>

        {/* Controls */}
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
         
          <button onClick={() => { localStorage.removeItem('display_token'); localStorage.removeItem('display_register_id'); setDisplayToken(''); setRegisterId(''); setBill(null); esRef.current?.close(); }}
  style={{ height:30, padding:'0 12px', borderRadius:15, border:`1px solid ${border}`, background:'transparent', color:textMuted, fontSize:11, fontWeight:600, cursor:'pointer' }}>
  ⚙ Reset
</button>
          <div style={{ height:30, padding:'0 12px', display:'flex', alignItems:'center', gap:6, borderRadius:15, border:`1px solid ${border}`, fontSize:11, fontWeight:600, color: connected ? '#22c55e' : '#ef4444' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background: connected ? '#22c55e' : '#ef4444', boxShadow: connected ? '0 0 6px #22c55e' : 'none' }} />
            {connected ? 'Live' : 'Offline'}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'72px 24px 40px' }}>

        {!bill ? (
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:56, marginBottom:16, opacity:0.15 }}>🧾</div>
            <div style={{ fontSize:18, fontWeight:700, color:textMuted }}>Ready for next customer</div>
            <div style={{ fontSize:13, color: darkMode ? '#334155' : '#cbd5e1', marginTop:6 }}>Waiting for new bill…</div>
          </div>
        ) : (
          <div
            style={{
            background: paid ? (darkMode ? '#0a1f0f' : '#f0fdf4') : cardBg,
            borderRadius:20, padding:'36px 32px', width:'100%', maxWidth: qrUrl && bill.payment_mode !== 'cash' && qrIsPoster ? 480 : 400,
            textAlign:'center',
            border:`1.5px solid ${paid ? '#22c55e' : border}`,
            boxShadow: paid
              ? '0 0 32px rgba(34,197,94,0.15)'
              : darkMode ? '0 24px 60px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.08)',
            transition:'all 0.35s',
            cursor: (!paid && !failed) ? 'pointer' : 'default',
          }}>

            {paid ? (
              <>
                <div style={{ fontSize:56, marginBottom:10 }}>✅</div>
                <div style={{ fontSize:22, fontWeight:900, color:'#22c55e' }}>Payment Received</div>
                <div style={{ fontSize:13, color:'#86efac', marginTop:6 }}>Thank you, {bill.customer_name}!</div>
              </>
            ) : failed ? (
              <>
                <div style={{ fontSize:56, marginBottom:10 }}>❌</div>
                <div style={{ fontSize:22, fontWeight:900, color:'#ef4444' }}>Payment Failed</div>
                <div style={{ fontSize:13, color:'#fca5a5', marginTop:6 }}>Please try again or pay at counter.</div>
                <button onClick={() => setFailed(false)} style={{ marginTop:16, height:36, padding:'0 20px', borderRadius:8, border:'1px solid #ef4444', background:'transparent', color:'#ef4444', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                  Dismiss
                </button>
              </>
            ) : (
              <>
                {/* Bill info */}
                <div style={{ fontSize:11, fontWeight:700, color:textMuted, letterSpacing:1, textTransform:'uppercase', marginBottom:2 }}>
                  {bill.bill_number}
                </div>
                <div style={{ fontSize:15, fontWeight:600, color:textPrimary, marginBottom:20 }}>
                  {bill.customer_name}
                </div>

                {/* Amount */}
                <div style={{ fontSize:52, fontWeight:900, color:textPrimary, lineHeight:1, marginBottom:10 }}>
                  {sym}{bill.grand_total.toFixed(2)}
                </div>

                {/* Payment mode badge */}
                <div style={{ display:'inline-flex', alignItems:'center', gap:5, background: darkMode ? '#1e293b' : '#f1f5f9', border:`1px solid ${border}`, borderRadius:20, padding:'4px 12px', fontSize:10, fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:1, marginBottom:24 }}>
                  {bill.payment_mode === 'cash' ? '💵' : bill.payment_mode === 'card' ? '💳' : '📱'} {bill.payment_mode}
                </div>

                {/* Payment not configured — explicit state instead of a dead QR */}
                {paymentUnavailable && bill.payment_mode !== 'cash' && (
                  <div style={{ marginBottom:8, padding:16, borderRadius:12, background: darkMode ? '#1e293b' : '#f8fafc', border:`1px dashed ${border}` }}>
                    <div style={{ fontSize:22, marginBottom:6 }}>⚠️</div>
                    <div style={{ fontSize:13, fontWeight:700, color:textPrimary, marginBottom:4 }}>
                      Online payment not set up
                    </div>
                    <div style={{ fontSize:11, color:textMuted }}>
                      Please pay at the counter, or ask staff to configure UPI / payment gateway in settings.
                    </div>
                  </div>
                )}

                {/* QR — only for online payment modes; cash is already settled at the counter */}
                {qrUrl && bill.payment_mode !== 'cash' && !paymentUnavailable && (
                  <div style={{ marginBottom:8 }}>
                    <div style={{ fontSize:11, color:textMuted, marginBottom:10 }}>Scan to pay online</div>
                    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', marginBottom:10 }}>
                      <img src={qrUrl} alt="Pay QR" style={{
                        width: qrIsPoster ? '100%' : 200,
                        height: qrIsPoster ? 'auto' : 200,
                        maxWidth: qrIsPoster ? 416 : undefined,
                        borderRadius:12, border:`3px solid ${border}`, background:'#fff',
                        padding: qrIsPoster ? 4 : 8,
                        display:'block', margin:'0 auto',
                      }} />
                    </div>
                    {qrContent && (
                      <div onClick={e => e.stopPropagation()} style={{ fontSize:10, color:textMuted, wordBreak:'break-all', background: darkMode ? '#0f172a' : '#f8fafc', border:`1px solid ${border}`, borderRadius:8, padding:'6px 10px', fontFamily:'monospace', userSelect:'all', cursor:'text' }}>
                        {qrContent}
                      </div>
                    )}
                    <div style={{ fontSize:10, color: darkMode ? '#334155' : '#cbd5e1', marginTop:8 }}>or pay at counter</div>
                  </div>
                )}

              </>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ position:'fixed', bottom:0, left:0, right:0, height:32, display:'flex', alignItems:'center', justifyContent:'center', borderTop:`1px solid ${border}`, background: darkMode ? 'rgba(15,23,42,0.98)' : 'rgba(255,255,255,0.98)' }}>
        <span style={{ fontSize:9, fontWeight:700, letterSpacing:2, color:textMuted, opacity:0.5 }}>POWERED BY CLOUDAIRAMBO™</span>
      </div>
    </div>
  );
}
